import React, { useState, useRef, useCallback, useEffect, useId } from 'react';
import PropTypes from 'prop-types';
import { isString, noop } from 'lodash';
import classnames from 'classnames';
import { useSelector } from 'react-redux';

import { Button } from 'components/core/Button';
import { DatePicker } from 'components/core/DatePicker';
import { GoogleAutocomplete } from 'components/core/GoogleAutocomplete';
import { Paragraph } from 'components/core/Typography';
import { ClickToShowNumberButton } from 'components/contact/ClickToShowNumberButton';
import { Search } from 'components/svg';
import './searchBox.scss';
import { trackCTAClick } from '../../core/library/tracking-utils/tracking/clickTrackingUtils';

// TODO: Recompose

export function SearchBox({
	handleSearch = noop,
	datePickerLabel = 'Check in',
	inputLabel = 'Enter address, city or zip',
	reservationWindow,
	moveInDate,
	location: locationProp = '',
	withDatePicker,
	darkButton,
	pinkButton,
	slpPinkButton,
	buttonText = 'Search',
	buttonIcon,
	id,
	hideOnScroll = false,
	clickToCall = false,
	getTollfreePhoneNumber,
	parentPhoneNumber,
	setParentPhoneNumber,
	parentPhoneLoaded,
	setParentPhoneLoaded,
	className = '',
	buttonClassName = '',
}) {
	const geolocation = useSelector((state) => state.user.geolocation || {});
	const stableId = useId();
	const instanceRef = useRef(null);

	const getDefaultLocation = () => {
		if (geolocation?.city || geolocation?.region) {
			return `${geolocation.city || ''}, ${geolocation.region || ''}`;
		}
		return '';
	};

	const [locationParams, setLocationParams] = useState({
		location: locationProp || getDefaultLocation(),
	});
	const [date, setDate] = useState(moveInDate);
	const [userHasInteracted, setUserHasInteracted] = useState(false);

	const prevLocationPropRef = useRef(locationProp);

	useEffect(() => {
		// Only update internal state if:
		// 1. The location prop has changed from parent
		// 2. AND the user hasn't manually interacted with the input
		// 3. AND it's actually different from current state
		if (
			prevLocationPropRef.current !== locationProp &&
			!userHasInteracted &&
			locationProp !== locationParams.location
		) {
			setLocationParams({ location: locationProp });
		}
		prevLocationPropRef.current = locationProp;
	}, [locationProp, userHasInteracted, locationParams.location]);

	const handleDateChange = useCallback((evt) => {
		const { value } = evt.target;
		setDate(value.date);
	}, []);

	const handleGoogle = useCallback((params) => {
		// Mark that user has interacted with the input
		// This prevents parent prop updates from overwriting user's selection
		setLocationParams(params);
		setUserHasInteracted(true);
	}, []);

	const handleSearchSubmit = useCallback((event) => {
		event.preventDefault();

		let searchParams;

		if (locationParams.locationSource) {
			searchParams = locationParams;
		} else if (instanceRef.current) {
			searchParams = { location: instanceRef.current.value };
		} else {
			searchParams = locationParams;
		}

		const searchInputId = id || inputLabel;

		/* istanbul ignore else */
		if (isString(searchParams.location) && searchParams.location.length) {
			trackCTAClick(
				searchInputId || 'search-box-id',
				'Search Button',
				buttonText?.props?.children ||
					buttonText ||
					'Search',
			);

			handleSearch({
				locationParams: searchParams,
				date,
				searchInputId,
			});
		}
	}, [locationParams, id, inputLabel, buttonText, handleSearch, date]);

	const setRef = useCallback((ref) => {
		instanceRef.current = ref;
	}, []);

	const ButtonIcon = buttonIcon;
	return (
		<div className={`search-box-container ${className}`}>
			{' '}
			{/* sf_12700_sfdc_paid_landing_refresh */}
			<form
				className={classnames('search-box', {
					'with-date': withDatePicker,
				})}
				onSubmit={handleSearchSubmit}
			>
				<GoogleAutocomplete
					label={inputLabel}
					// Adding an automatically generated ID to avoid a an iOS autofocus bug that happens
					// when there is more than one input field with the same ID on a page
					id={id || `location-${stableId}`}
					className="location"
					name="location"
					onChange={handleGoogle}
					getRef={setRef}
					value={locationParams.location}
					hideOnScroll={hideOnScroll} // sf_11267_sticky_search_clp_hero
				/>
				<div className="date-search-wrapper">
					{!!withDatePicker && (
						<DatePicker
							label={datePickerLabel}
							id="date"
							className="date"
							name="date"
							value=""
							reservationWindow={reservationWindow}
							selectedDate={date}
							onChange={handleDateChange}
						/>
					)}
					<Button
						className={classnames('search-button', buttonClassName)}
						pink={pinkButton}
						dark={darkButton}
						slpPink={slpPinkButton}
						type="submit"
					>
						{!!ButtonIcon && <Search />}
						{buttonText}
					</Button>
				</div>
			</form>
			{clickToCall && ( // sf_12700_sfdc_paid_landing_refresh
				<div className="call-container">
					<Paragraph
						last
						color="black"
						weight="ultraBold"
					>
						OR
					</Paragraph>
					<ClickToShowNumberButton
						outline
						fetchPhoneNumber={getTollfreePhoneNumber}
						showAsLink
						size="small"
						label="Call to Find Storage"
						// sf_12700_sfdc_paid_landing_refresh
						parentPhoneNumber={parentPhoneNumber}
						setParentPhoneNumber={setParentPhoneNumber}
						parentPhoneLoaded={parentPhoneLoaded}
						setParentPhoneLoaded={setParentPhoneLoaded}
					/>
				</div>
			)}
		</div>
	);
}

SearchBox.propTypes = {
	handleSearch: PropTypes.func,
	datePickerLabel: PropTypes.string,
	inputLabel: PropTypes.string,
	reservationWindow: PropTypes.instanceOf(Date),
	moveInDate: PropTypes.instanceOf(Date),
	location: PropTypes.string,
	withDatePicker: PropTypes.bool,
	darkButton: PropTypes.bool,
	pinkButton: PropTypes.bool,
	slpPinkButton: PropTypes.bool,
	buttonText: PropTypes.node,
	buttonIcon: PropTypes.bool,
	id: PropTypes.string,
	hideOnScroll: PropTypes.bool,
	clickToCall: PropTypes.bool,
	getTollfreePhoneNumber: PropTypes.func,
	parentPhoneNumber: PropTypes.object,
	setParentPhoneNumber: PropTypes.func,
	parentPhoneLoaded: PropTypes.func,
	setParentPhoneLoaded: PropTypes.func,
	className: PropTypes.string,
	buttonClassName: PropTypes.string,
	geolocation: PropTypes.shape({
		city: PropTypes.string,
		region: PropTypes.string,
	}),
};

export default SearchBox;
